import React from "react";
import { Icon } from "@iconify/react";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../../components/utils";

interface Props {
  htmlContent: string;
  systemVars: string[];
  customVars: string[];
  suspiciousVars: string[];
  onNext: () => void;
  onBack: () => void;
}

export default function Step3Variables({
  systemVars,
  customVars,
  suspiciousVars,
  onNext,
  onBack,
}: Props) {
  return (
    <VStack className="gap-5 p-6 h-full overflow-y-auto">
      <p className="text-sm text-gray-600">
        Variables found in your template. System variables are resolved
        automatically. Custom variables need per-employee values (filled via
        Excel in Step 4).
      </p>

      {suspiciousVars.length > 0 && (
        <VStack className="gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <HStack className="items-center gap-2">
            <Icon
              icon="mdi:alert-circle-outline"
              className="text-red-600"
              height={16}
            />
            <p className="text-sm font-semibold text-red-700">
              Unrecognized Variables — Will NOT Be Replaced
            </p>
          </HStack>
          <p className="text-xs text-red-600">
            These use dot notation but are not recognized system variables. Go
            back and remove them or replace with a valid system variable or a
            simple <code className="bg-red-100 px-1 rounded">#customVar#</code>{" "}
            (no dots).
          </p>
          <div className="flex flex-wrap gap-2 mt-1">
            {suspiciousVars.map((v) => (
              <span
                key={v}
                className="text-xs font-mono bg-red-100 text-red-800 border border-red-300 px-2 py-1 rounded"
              >
                {v}
              </span>
            ))}
          </div>
        </VStack>
      )}

      <VStack className="gap-3">
        <p className="text-sm font-semibold text-gray-700">
          System Variables{" "}
          <span className="ml-1 text-xs font-normal text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
            Auto-resolved
          </span>
        </p>
        {systemVars.length === 0 ? (
          <p className="text-xs text-gray-400 italic">
            No system variables found in template.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {systemVars.map((v) => (
              <span
                key={v}
                className="text-xs font-mono bg-green-50 text-green-800 border border-green-200 px-2 py-1 rounded"
              >
                {v}
              </span>
            ))}
          </div>
        )}
      </VStack>

      <VStack className="gap-3">
        <p className="text-sm font-semibold text-gray-700">
          Custom Variables{" "}
          <span className="ml-1 text-xs font-normal text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
            Fill per employee
          </span>
        </p>
        {customVars.length === 0 ? (
          <p className="text-xs text-gray-400 italic">
            No custom variables found.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {customVars.map((v) => (
              <span
                key={v}
                className="text-xs font-mono bg-orange-50 text-orange-800 border border-orange-200 px-2 py-1 rounded"
              >
                {v}
              </span>
            ))}
          </div>
        )}
      </VStack>

      <HStack className="flex-row-reverse gap-3 mt-auto">
        <ButtonPrimary
          onClick={onNext}
          disabled={suspiciousVars.length > 0}
          title={
            suspiciousVars.length > 0
              ? "Fix unrecognized variables before continuing"
              : undefined
          }
        >
          Continue
        </ButtonPrimary>
        <ButtonSecondary onClick={onBack}>Back</ButtonSecondary>
      </HStack>
    </VStack>
  );
}
